import { formatApiErrorDetail } from "@/lib/api";
/**
 * Tradesman Portal — mobile-first magic-link surface for any on-site helper
 * (cleaner, plumber, electrician, painter, HVAC, handyman, etc). Photo +
 * status only. Fully redacted view (no cost, no docs, no landlord metadata).
 *
 * If the link was minted with a PIN, first visit on each device is gated by
 * a 4-digit PIN prompt. Once entered, the device_id is remembered on both
 * sides (localStorage on frontend, known_devices[] on the project row) so
 * subsequent visits go straight to the punch list for 30 days.
 *
 * Bilingual (EN/ES). Auto-detects browser language on first visit; sticky
 * toggle in the header lets the tradesman flip.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  Camera, ImageSquare, CheckCircle, ArrowClockwise,
  Sparkle, WarningCircle, Lock, SignOut,
} from "@phosphor-icons/react";
import { useT, PublicLangToggle } from "@/i18n";

const BASE = process.env.REACT_APP_BACKEND_URL;
const DEVICE_KEY = "fr_tradesman_device_id";

function getDeviceId() {
  try {
    let d = localStorage.getItem(DEVICE_KEY);
    if (!d) {
      d = "dev_" + Math.random().toString(36).slice(2, 10) + "_" + Date.now().toString(36);
      localStorage.setItem(DEVICE_KEY, d);
    }
    return d;
  } catch { return "anonymous"; }
}

const TRADE_EMOJI = {
  cleaner: "🧹", plumber: "🔧", electrician: "⚡", painter: "🎨",
  hvac: "❄️", handyman: "🔨", landscaper: "🌿", roofer: "🏠",
  water_heater: "💧", general_sub: "👷",
};

function StatusPill({ status, t }) {
  const cls = {
    open:        "bg-amber-100 text-amber-900 border-amber-500",
    in_progress: "bg-blue-100 text-blue-900 border-blue-500",
    complete:    "bg-emerald-100 text-emerald-900 border-emerald-500",
    deferred:    "bg-slate-100 text-slate-700 border-slate-400",
  }[status] || "bg-amber-100 text-amber-900 border-amber-500";
  const label = {
    open:        t("tp.status.open"),
    in_progress: t("tp.status.in_progress"),
    complete:    t("tp.status.complete"),
    deferred:    t("pub.status.deferred"),
  }[status] || status;
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-black uppercase tracking-widest border ${cls}`}>{label}</span>;
}

function PhotoThumb({ path }) {
  return <img src={`${BASE}/api/files/${path}`} alt="proof"
    className="w-16 h-16 object-cover border-2 border-[#0f172a]" loading="lazy"/>;
}

function PinPrompt({ label, trade, onSubmit, error }) {
  const { t } = useT();
  const [pin, setPin] = useState("");
  const emoji = TRADE_EMOJI[trade] || "👷";
  const tradeLabel = t(`tp.trade.${trade || "general_sub"}`);
  return (
    <div className="min-h-screen bg-[#f4f4f5] flex items-center justify-center p-4" data-testid="pin-prompt">
      <div className="bg-white border-4 border-[#0f172a] shadow-[8px_8px_0px_#00C4CC] max-w-sm w-full p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Lock weight="fill" size={22} className="text-[#00C4CC]"/>
            <div className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC]">
              {t("tp.pin_title")}
            </div>
          </div>
          <PublicLangToggle dark={false}/>
        </div>
        <div className="font-display font-black text-2xl uppercase leading-tight mb-1">
          {emoji} {label || tradeLabel}
        </div>
        <div className="text-xs text-slate-600 mb-4">{t("tp.pin_body")}</div>
        <input value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
          data-testid="pin-input"
          inputMode="numeric" autoComplete="one-time-code"
          placeholder="••••"
          className="w-full h-14 border-2 border-[#0f172a] text-center text-3xl font-black tracking-[0.6em] mb-3"
          onKeyDown={e => e.key === "Enter" && pin.length === 4 && onSubmit(pin)}/>
        {error && (
          <div className="p-2 bg-red-100 border-2 border-red-500 text-red-900 text-xs font-black uppercase tracking-widest mb-3 text-center">
            {error}
          </div>
        )}
        <button onClick={() => onSubmit(pin)} disabled={pin.length !== 4}
          data-testid="pin-submit"
          className="w-full h-12 bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-sm tracking-widest disabled:opacity-40">
          {t("tp.pin_unlock")}
        </button>
        <div className="mt-4 text-[10px] font-black uppercase tracking-widest text-slate-400 text-center">
          {t("tp.footer")}
        </div>
      </div>
    </div>
  );
}

function ItemCard({ token, deviceId, item, onChanged }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const upload = async (files, source) => {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await axios.post(
          `${BASE}/api/public/tradesman/${token}/punch/${item.id}/photo?device_id=${deviceId}`,
          fd, { headers: { "Content-Type": "multipart/form-data" } },
        );
      }
      const n = files.length;
      toast.success(t("tp.photos_added", { n, plural: n === 1 ? "" : "s", source }));
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? t("tp.upload_fail")));
    } finally { setBusy(false); }
  };

  const flip = async (status) => {
    if (item.status === status) return;
    setBusy(true);
    try {
      await axios.patch(
        `${BASE}/api/public/tradesman/${token}/punch/${item.id}/status`,
        { status, device_id: deviceId },
      );
      toast.success(status === "complete" ? t("tp.marked_complete") : t("tp.marked_inprogress"));
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? t("tp.status_fail")));
    } finally { setBusy(false); }
  };

  const photos = item.photos || [];
  const needMore = Math.max(0, (item.min_photos || 0) - photos.length);
  const prioLabel = {
    urgent: t("tp.priority.urgent"), high: t("tp.priority.high"),
    med: t("tp.priority.med"), low: t("tp.priority.low"),
  }[item.priority] || item.priority;

  return (
    <div className="bg-white border-2 border-[#0f172a] shadow-[4px_4px_0px_#0f172a]" data-testid={`tradesman-item-${item.id}`}>
      <div className="p-3 border-b-2 border-slate-100">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <StatusPill status={item.status} t={t}/>
          {item.priority && (
            <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 ${
              item.priority === "urgent" ? "bg-red-600 text-white" :
              item.priority === "high" ? "bg-orange-500 text-white" :
              "bg-slate-200 text-slate-700"
            }`}>
              {prioLabel}
            </span>
          )}
          {item.category && <span className="text-[9px] font-black uppercase tracking-widest text-slate-500">{item.category}</span>}
        </div>
        <div className="font-display font-black text-base leading-tight">{item.title}</div>
        {item.description && <div className="text-xs text-slate-600 mt-1">{item.description}</div>}
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-1">📍 {item.room}</div>
      </div>

      {photos.length > 0 && (
        <div className="p-2 border-b-2 border-slate-100 flex gap-1 overflow-x-auto">
          {photos.map((p, i) => <PhotoThumb key={i} path={p}/>)}
        </div>
      )}

      {needMore > 0 && (
        <div className="px-3 py-2 bg-amber-50 border-b-2 border-amber-300 text-[11px] font-black uppercase tracking-widest text-amber-800">
          {t("tp.need_more", { n: needMore, plural: needMore === 1 ? "" : "s" })}
        </div>
      )}

      <div className="p-2 grid grid-cols-2 gap-1">
        <button onClick={() => cameraRef.current?.click()} disabled={busy}
          data-testid={`tradesman-camera-${item.id}`}
          className="h-11 px-2 bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center justify-center gap-1 disabled:opacity-40">
          <Camera weight="fill" size={16}/> {t("tp.btn.camera")}
        </button>
        <button onClick={() => galleryRef.current?.click()} disabled={busy}
          data-testid={`tradesman-gallery-${item.id}`}
          className="h-11 px-2 bg-slate-700 hover:bg-slate-800 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center justify-center gap-1 disabled:opacity-40">
          <ImageSquare weight="fill" size={16}/> {t("tp.btn.gallery")}
        </button>
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" multiple hidden
          onChange={e => { upload(e.target.files, t("tp.source.camera")); e.target.value = ""; }}/>
        <input ref={galleryRef} type="file" accept="image/*" multiple hidden
          onChange={e => { upload(e.target.files, t("tp.source.gallery")); e.target.value = ""; }}/>
      </div>

      <div className="p-2 border-t-2 border-slate-100 grid grid-cols-2 gap-1">
        <button onClick={() => flip("in_progress")} disabled={busy || item.status === "in_progress"}
          data-testid={`tradesman-inprog-${item.id}`}
          className={`h-10 px-2 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-1 disabled:opacity-40 ${
            item.status === "in_progress" ? "bg-blue-500 text-white" : "bg-white hover:bg-blue-50 text-blue-800"
          }`}>
          <ArrowClockwise weight="bold" size={12}/> {t("tp.btn.inprog")}
        </button>
        <button onClick={() => flip("complete")} disabled={busy || item.status === "complete"}
          data-testid={`tradesman-complete-${item.id}`}
          className={`h-10 px-2 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-1 disabled:opacity-40 ${
            item.status === "complete" ? "bg-emerald-600 text-white" : "bg-white hover:bg-emerald-50 text-emerald-800"
          }`}>
          <CheckCircle weight="bold" size={12}/> {t("tp.btn.complete")}
        </button>
      </div>
    </div>
  );
}

export default function TradesmanPortal() {
  const { t } = useT();
  const { token } = useParams();
  const deviceId = useMemo(() => getDeviceId(), []);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [pinRequired, setPinRequired] = useState(false);
  const [pinMeta, setPinMeta] = useState({});
  const [pinError, setPinError] = useState("");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("open");
  const [unitFilter, setUnitFilter] = useState("all"); // "all" | unit name | "__common__" | "__shared__"

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${BASE}/api/public/tradesman/${token}?device_id=${deviceId}`);
      setData(r.data);
      setErr("");
      setPinRequired(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 401 && d?.code === "PIN_REQUIRED") {
        setPinRequired(true);
        setPinMeta(d);
        setPinError("");
      } else {
        setErr(typeof d === "string" ? d : t("tp.link_broken_body"));
        setData(null);
      }
    }
  }, [token, deviceId, t]);
  useEffect(() => { load(); }, [load]);

  const submitPin = async (pin) => {
    setPinError("");
    try {
      const r = await axios.post(`${BASE}/api/public/tradesman/${token}/pin`,
        { pin, device_id: deviceId });
      toast.success(t("tp.unlocked"));
      if (r.data.shared_warning) toast.warning(t("tp.shared_warning"));
      load();
    } catch (e) {
      setPinError(e.response?.data?.detail || t("tp.pin_wrong"));
    }
  };

  const items = data?.punch_items || [];

  // Classify a room name into: "unit" (e.g. 8A, 12C), "common" (Bldg X Common), or "shared" (Exterior, All Units, other)
  const classifyRoom = (name) => {
    if (!name) return "shared";
    if (/^\d{1,2}[A-Z]$/i.test(name.trim())) return "unit";
    if (/common/i.test(name)) return "common";
    return "shared";
  };
  const roomSortKey = (name) => {
    const m = (name || "").match(/^(\d{1,2})([A-Z])$/i);
    if (m) return [0, parseInt(m[1], 10), m[2].toUpperCase()];
    const b = (name || "").match(/Bldg\s+(\d+)/i);
    if (b) return [1, parseInt(b[1], 10), name];
    return [2, 0, name || ""];
  };

  // Unit summary chips: total counts per unit AFTER status filter but BEFORE search-filter.
  // Search should never hide unit chips (they're navigation, not results).
  const unitSummary = useMemo(() => {
    let list = items;
    if (filter === "open") list = list.filter(i => i.status !== "complete");
    else if (filter === "complete") list = list.filter(i => i.status === "complete");
    const rooms = {};
    for (const it of list) {
      const room = (it.room || "Unassigned").trim();
      const rec = rooms[room] = rooms[room] || { name: room, kind: classifyRoom(room), n: 0, open: 0 };
      rec.n += 1;
      if (it.status !== "complete") rec.open += 1;
    }
    return Object.values(rooms).sort((a, b) => {
      const A = roomSortKey(a.name), B = roomSortKey(b.name);
      return (A[0] - B[0]) || (A[1] - B[1]) || String(A[2]).localeCompare(String(B[2]));
    });
  }, [items, filter]);

  const grouped = useMemo(() => {
    let list = items;
    if (filter === "open") list = list.filter(i => i.status !== "complete");
    else if (filter === "complete") list = list.filter(i => i.status === "complete");
    if (q.trim()) {
      const n = q.trim().toLowerCase();
      list = list.filter(i =>
        (i.title || "").toLowerCase().includes(n) ||
        (i.room || "").toLowerCase().includes(n) ||
        (i.description || "").toLowerCase().includes(n),
      );
    }
    if (unitFilter === "__common__") list = list.filter(i => classifyRoom(i.room) === "common");
    else if (unitFilter === "__shared__") list = list.filter(i => classifyRoom(i.room) === "shared");
    else if (unitFilter !== "all") list = list.filter(i => (i.room || "").trim() === unitFilter);
    const groups = {};
    for (const it of list) {
      const room = (it.room || "Unassigned").trim();
      (groups[room] = groups[room] || []).push(it);
    }
    return groups;
  }, [items, filter, q, unitFilter]);

  const sortedRoomKeys = useMemo(() => Object.keys(grouped).sort((a, b) => {
    const A = roomSortKey(a), B = roomSortKey(b);
    return (A[0] - B[0]) || (A[1] - B[1]) || String(A[2]).localeCompare(String(B[2]));
  }), [grouped]);

  const units = unitSummary.filter(r => r.kind === "unit");
  const commons = unitSummary.filter(r => r.kind === "common");
  const shareds = unitSummary.filter(r => r.kind === "shared");

  const kpis = data?.kpis || {};

  if (pinRequired) {
    return (
      <>
        <Toaster richColors position="top-center"/>
        <PinPrompt label={pinMeta.label} trade={pinMeta.trade}
          onSubmit={submitPin} error={pinError}/>
      </>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen bg-[#f4f4f5] flex items-center justify-center p-6" data-testid="tradesman-error">
        <div className="bg-white border-4 border-[#0f172a] shadow-[8px_8px_0px_#dc2626] p-8 max-w-sm text-center">
          <div className="flex justify-end mb-2"><PublicLangToggle dark={false}/></div>
          <WarningCircle weight="fill" size={40} className="mx-auto text-red-600 mb-2"/>
          <div className="font-display font-black text-xl uppercase">{t("tp.link_broken")}</div>
          <div className="text-sm text-slate-600 mt-2">{err}</div>
          <div className="text-xs text-slate-500 mt-4">{t("tp.link_broken_body")}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen bg-[#f4f4f5] flex items-center justify-center font-black uppercase text-slate-400">{t("common.loading")}</div>;
  }

  const trade = data.tradesman?.trade || "general_sub";
  const tradeLabel = t(`tp.trade.${trade}`);
  const tradeEmoji = TRADE_EMOJI[trade] || "👷";

  return (
    <div className="min-h-screen bg-[#f4f4f5] pb-20" data-testid="tradesman-portal">
      <Toaster richColors position="top-center"/>

      <header className="sticky top-0 z-10 bg-[#0f172a] text-white p-4 shadow-lg">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            <Sparkle weight="fill" size={16} className="text-[#00C4CC]"/>
            <div className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC]">
              Field Radar · {t("tp.header_link", { trade: tradeLabel })}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <PublicLangToggle/>
            <button
              type="button"
              data-testid="tradesman-signout-btn"
              onClick={() => {
                if (!window.confirm(t("tp.signout_confirm"))) return;
                try { localStorage.removeItem(DEVICE_KEY); } catch {}
                window.location.href = "/tradesman-signed-out";
              }}
              title={t("tp.signout")}
              className="h-8 w-8 inline-flex items-center justify-center bg-slate-800 hover:bg-red-600 text-white border border-white/20"
            >
              <SignOut weight="bold" size={14}/>
            </button>
          </div>
        </div>
        <div className="font-display font-black text-lg leading-tight" data-testid="tradesman-project-name">
          {data.name}
        </div>
        <div className="text-xs text-slate-300 truncate">
          {data.property_address}{data.unit ? ` · Unit ${data.unit}` : ""}
        </div>
        {data.tradesman?.label && (
          <div className="mt-2 inline-block px-2 py-0.5 bg-[#00C4CC] text-[#0f172a] text-[10px] font-black uppercase tracking-widest">
            👋 {tradeEmoji} {data.tradesman.label}
          </div>
        )}
      </header>

      <div className="p-3 grid grid-cols-3 gap-2">
        <button onClick={() => setFilter("open")} data-testid="tradesman-filter-open"
          className={`p-2 border-2 border-[#0f172a] text-left ${filter === "open" ? "bg-amber-500 text-white" : "bg-white text-slate-700"}`}>
          <div className="text-[9px] font-black uppercase tracking-widest opacity-80">{t("tp.filter.open")}</div>
          <div className="font-display font-black text-xl">{(kpis.open || 0) + (kpis.in_progress || 0)}</div>
        </button>
        <button onClick={() => setFilter("complete")} data-testid="tradesman-filter-complete"
          className={`p-2 border-2 border-[#0f172a] text-left ${filter === "complete" ? "bg-emerald-600 text-white" : "bg-white text-slate-700"}`}>
          <div className="text-[9px] font-black uppercase tracking-widest opacity-80">{t("tp.filter.complete")}</div>
          <div className="font-display font-black text-xl">{kpis.complete || 0}</div>
        </button>
        <button onClick={() => setFilter("all")} data-testid="tradesman-filter-all"
          className={`p-2 border-2 border-[#0f172a] text-left ${filter === "all" ? "bg-[#0f172a] text-white" : "bg-white text-slate-700"}`}>
          <div className="text-[9px] font-black uppercase tracking-widest opacity-80">{t("tp.filter.all")}</div>
          <div className="font-display font-black text-xl">{kpis.total || 0}</div>
        </button>
      </div>

      <div className="px-3 pb-2">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t("tp.search")}
          data-testid="tradesman-search"
          className="w-full h-11 border-2 border-[#0f172a] px-3 text-sm bg-white"/>
      </div>

      {/* Sticky unit-selector strip — one tap to jump to a unit / building common / exterior */}
      {(units.length > 0 || commons.length > 0 || shareds.length > 0) && (
        <div className="sticky top-[112px] z-[6] bg-[#0f172a] border-y-2 border-[#00C4CC] pt-2 pb-2 px-3 shadow-md"
          data-testid="tradesman-unit-bar">
          <div className="text-[9px] font-black uppercase tracking-widest text-[#00C4CC] mb-1.5">📍 Jump to</div>
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" style={{ scrollbarWidth: "thin" }}>
            <button onClick={() => setUnitFilter("all")} data-testid="tradesman-unit-all"
              className={`h-9 px-3 border-2 font-black uppercase text-[10px] tracking-widest whitespace-nowrap flex-shrink-0 ${
                unitFilter === "all" ? "bg-[#00C4CC] border-[#00C4CC] text-[#0f172a]" : "bg-transparent border-white/40 text-white hover:bg-white/10"
              }`}>All · {unitSummary.reduce((s, r) => s + r.n, 0)}</button>
            {units.map(u => (
              <button key={u.name} onClick={() => setUnitFilter(u.name)}
                data-testid={`tradesman-unit-${u.name}`}
                className={`h-9 px-2.5 border-2 font-black uppercase text-[10px] tracking-widest whitespace-nowrap flex-shrink-0 inline-flex items-center gap-1 ${
                  unitFilter === u.name ? "bg-fuchsia-500 border-fuchsia-500 text-white" :
                  u.open === 0 ? "bg-emerald-600/20 border-emerald-500/60 text-emerald-200 hover:bg-emerald-500/30" :
                  "bg-transparent border-white/40 text-white hover:bg-white/10"
                }`}>
                <span>{u.name}</span>
                <span className={`px-1 text-[9px] ${
                  u.open === 0 ? "bg-emerald-600 text-white" :
                  unitFilter === u.name ? "bg-white/30 text-white" : "bg-white/20 text-white"
                }`}>{u.open === 0 ? "✓" : u.open}</span>
              </button>
            ))}
            {commons.length > 0 && (
              <button onClick={() => setUnitFilter("__common__")} data-testid="tradesman-unit-common"
                className={`h-9 px-2.5 border-2 font-black uppercase text-[10px] tracking-widest whitespace-nowrap flex-shrink-0 ${
                  unitFilter === "__common__" ? "bg-sky-500 border-sky-500 text-white" : "bg-transparent border-white/40 text-white hover:bg-white/10"
                }`}>Commons · {commons.reduce((s, r) => s + r.n, 0)}</button>
            )}
            {shareds.length > 0 && (
              <button onClick={() => setUnitFilter("__shared__")} data-testid="tradesman-unit-shared"
                className={`h-9 px-2.5 border-2 font-black uppercase text-[10px] tracking-widest whitespace-nowrap flex-shrink-0 ${
                  unitFilter === "__shared__" ? "bg-orange-500 border-orange-500 text-white" : "bg-transparent border-white/40 text-white hover:bg-white/10"
                }`}>Exterior · {shareds.reduce((s, r) => s + r.n, 0)}</button>
            )}
          </div>
        </div>
      )}

      <div className="px-3 pt-3 space-y-5">
        {sortedRoomKeys.length === 0 ? (
          <div className="p-8 bg-white border-2 border-dashed border-slate-300 text-center">
            <div className="font-display font-black text-lg uppercase">{t("tp.all_caught_up")}</div>
            <div className="text-xs text-slate-500 mt-1">{t("tp.nothing_matches")}</div>
          </div>
        ) : sortedRoomKeys.map(room => {
          const list = grouped[room];
          const n = list.length;
          const kind = classifyRoom(room);
          const openN = list.filter(x => x.status !== "complete").length;
          const doneN = n - openN;
          const headerBg =
            kind === "unit" ? "bg-fuchsia-600" :
            kind === "common" ? "bg-sky-600" :
            "bg-orange-600";
          const icon = kind === "unit" ? "🏠" : kind === "common" ? "🏢" : "🌳";
          return (
            <section key={room} data-testid={`tradesman-room-${room}`}>
              {/* Thicker unit header bar for scan-ability */}
              <div className={`sticky top-[172px] z-[5] ${headerBg} text-white px-3 py-2 border-2 border-[#0f172a] shadow-[3px_3px_0px_#0f172a] mb-2 flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                  <span className="text-lg">{icon}</span>
                  <div>
                    <div className="font-display font-black text-lg leading-tight uppercase tracking-tight">{room}</div>
                    <div className="text-[9px] font-black uppercase tracking-widest opacity-80">
                      {openN > 0 ? `${openN} to do` : "All complete"}{doneN > 0 && openN > 0 ? ` · ${doneN} done` : ""}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {doneN > 0 && (
                    <span className="px-1.5 py-0.5 bg-white/25 text-white text-[10px] font-black uppercase tracking-widest">✓ {doneN}</span>
                  )}
                  <span className="px-1.5 py-0.5 bg-white text-[#0f172a] text-[10px] font-black uppercase tracking-widest">{n}</span>
                </div>
              </div>
              <div className="space-y-2">
                {list.map(it => (
                  <ItemCard key={it.id} token={token} deviceId={deviceId} item={it} onChanged={load}/>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <footer className="mt-8 px-3 pb-8">
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 text-center">
          {t("tp.footer")}
        </div>
      </footer>
    </div>
  );
}
